import React, { useEffect, useState } from "react";
import jwt_decode from "jwt-decode";
import axios from 'axios';
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import apiConfig from '../../apiConfig';

/* ── inline styles object (no extra CSS file needed) ── */
const S = {
  page: {
	fontFamily: "'Outfit', sans-serif",
	background: '#F7F9FC',
	minHeight: '100vh',
	padding: '28px 24px 80px',
  },

  /* ── Welcome banner ── */
  banner: {
	background: 'linear-gradient(120deg, #0066CC 0%, #0052A3 100%)',
	borderRadius: '14px',
	padding: '22px 28px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	flexWrap: 'wrap',
	gap: '14px',
	marginBottom: '22px',
	boxShadow: '0 4px 20px rgba(0,102,204,0.2)',
	position: 'relative',
	overflow: 'hidden',
  },
  bannerBubble: {
	position: 'absolute',
	borderRadius: '50%',
	background: 'rgba(255,255,255,0.06)',
	pointerEvents: 'none',
  },
  bannerLeft: { position: 'relative', zIndex: 1 },
  bannerGreet: {
	fontSize: '13px',
	fontWeight: 500,
	color: 'rgba(255,255,255,0.7)',
	marginBottom: '4px',
	letterSpacing: '0.03em',
	textTransform: 'uppercase',
  },
  bannerTitle: {
	fontSize: '22px',
	fontWeight: 800,
	color: '#FFFFFF',
	margin: 0,
	letterSpacing: '-0.3px',
  },
  bannerRole: { color: '#FFC107' },
  bannerUID: {
	marginTop: '6px',
	fontSize: '12px',
	color: 'rgba(255,255,255,0.55)',
	fontWeight: 500,
  },
  bannerActions: {
	display: 'flex',
	gap: '8px',
	flexWrap: 'wrap',
	position: 'relative',
	zIndex: 1,
  },

  /* ── Buttons ── */
  btn: (bg, color = '#fff') => ({
	display: 'inline-flex',
	alignItems: 'center',
	gap: '6px',
	padding: '9px 16px',
	borderRadius: '9px',
	fontSize: '13px',
	fontWeight: 600,
	textDecoration: 'none',
	border: 'none',
	cursor: 'pointer',
	background: bg,
	color,
	fontFamily: "'Outfit', sans-serif",
	whiteSpace: 'nowrap',
	transition: 'opacity 0.15s',
  }),

  /* ── Stat cards grid ── */
  grid: {
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
	gap: '16px',
	marginBottom: '22px',
  },
  card: {
	background: '#FFFFFF',
	borderRadius: '12px',
	padding: '18px 20px',
	border: '1px solid #EFEFEF',
	boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
	transition: 'box-shadow 0.15s, transform 0.15s',
	cursor: 'default',
  },
  cardIcon: (bg, color) => ({
	width: '38px',
	height: '38px',
	borderRadius: '10px',
	background: bg,
	color,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	fontSize: '17px',
	marginBottom: '12px',
  }),
  cardLabel: {
	fontSize: '11px',
	fontWeight: 700,
	color: '#BBBBBB',
	textTransform: 'uppercase',
	letterSpacing: '0.07em',
	marginBottom: '4px',
  },
  cardValue: {
	fontSize: '22px',
	fontWeight: 800,
	color: '#111',
	letterSpacing: '-0.5px',
  },
  cardUnit: {
	fontSize: '12px',
	fontWeight: 600,
	color: '#999',
	marginLeft: '3px',
  },

  /* ── Referral box ── */
  refBox: {
	background: '#FFFFFF',
	borderRadius: '12px',
	padding: '18px 20px',
	border: '1px solid #EFEFEF',
	marginTop: '16px',
  },
  refLabel: {
	fontSize: '11px',
	fontWeight: 700,
	color: '#BBBBBB',
	textTransform: 'uppercase',
	letterSpacing: '0.07em',
	marginBottom: '10px',
  },
  refRow: {
	display: 'flex',
	gap: '8px',
	alignItems: 'center',
  },
  refInput: {
	flex: 1,
	padding: '9px 13px',
	borderRadius: '8px',
	border: '1px solid #EFEFEF',
	fontSize: '13px',
	color: '#555',
	background: '#F7F9FC',
	fontFamily: "'Outfit', sans-serif",
	outline: 'none',
	minWidth: 0,
  },

  /* ── Chat table ── */
  tableWrap: {
	background: '#FFFFFF',
	borderRadius: '12px',
	border: '1px solid #EFEFEF',
	overflow: 'hidden',
	marginBottom: '22px',
  },
  tableHead: {
	background: '#F7F9FC',
	borderBottom: '1px solid #EFEFEF',
	padding: '12px 20px',
	fontSize: '11px',
	fontWeight: 700,
	color: '#BBBBBB',
	textTransform: 'uppercase',
	letterSpacing: '0.07em',
	display: 'grid',
	gridTemplateColumns: '48px 1fr 80px',
	gap: '12px',
  },
  tableRow: {
	display: 'grid',
	gridTemplateColumns: '48px 1fr 80px',
	gap: '12px',
	padding: '12px 20px',
	alignItems: 'center',
	borderBottom: '1px solid #F7F9FC',
	fontSize: '13px',
	color: '#333',
  },
  badge: (bg, color) => ({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	minWidth: '22px',
	height: '22px',
	borderRadius: '11px',
	background: bg,
	color,
	fontSize: '11px',
	fontWeight: 700,
	padding: '0 6px',
  }),

  sectionTitle: {
	fontSize: '13px',
	fontWeight: 700,
	color: '#BBBBBB',
	textTransform: 'uppercase',
	letterSpacing: '0.07em',
	marginBottom: '12px',
	marginTop: '4px',
  },
};

/* ── Stat card component ── */
const StatCard = ({ label, value, unit, iconClass, iconBg, iconColor, linkTo }) => {
  const inner = (
	<div style={S.card}
	  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
	  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
	>
	  <div style={S.cardIcon(iconBg, iconColor)}>
		<i className={`material-icons`} style={{ fontSize: '18px' }}>{iconClass}</i>
	  </div>
	  <div style={S.cardLabel}>{label}</div>
	  <div style={S.cardValue}>
		{value}
		{unit && <span style={S.cardUnit}>{unit}</span>}
	  </div>
	</div>
  );
  return linkTo ? <Link to={linkTo} style={{ textDecoration: 'none' }}>{inner}</Link> : inner;
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════ */
const DashContent = () => {
  const token = localStorage.getItem("jwtToken");
  const decodedToken = jwt_decode(token);
  const userInfo = decodedToken;
  const userID = userInfo.user_id;
  const user_role = userInfo.role_as;
  const user_id = userInfo.user_id;
  const role_as = userInfo.role_as;

  function isTokenExpired(token) {
	const decoded = jwt_decode(token);
	return decoded.exp < Date.now() / 1000;
  }
  const loggedinToken = localStorage.getItem("jwtToken");
  if (loggedinToken && isTokenExpired(loggedinToken)) {
	localStorage.removeItem("jwtToken");
	window.location.href = "/login";
  }

  const [data, setDataAxios] = useState(null);
  const [agentdata, setAgentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const [agentDashboardData, setAgentDashboardData] = useState({
	received_from_admin: 0,
	total_Transfer_Agents: 0,
	total_Downline_Balance: 0,
	total_agent_Downline_Count: 0,
	total_Downline_Count: 0,
  });

  useEffect(() => {
	const fetchData = async () => {
	  try {
		const response = await axios.get(`${apiConfig.baseURL}/api/users/shows/${userID}`);
		setDataAxios(response.data);
	  } catch (error) { console.error(error); }
	};
	fetchData();
	fetchAdminData();
  }, [userID]);

  const fetchAdminData = async () => {
	try {
	  const response = await axios.get(`${apiConfig.baseURL}/api/agent/agentData/${userID}`);
	  setAgentDashboardData(response.data);
	} catch (error) { console.error(error); }
  };

  useEffect(() => {
	const getAllUser = async () => {
	  setIsLoading(true);
	  try {
		await axios.post(`${apiConfig.baseURL}/api/broadcast/new_chats`, { user_id, role_as });
		setAgentData([]);
		setIsLoading(false);
	  } catch (error) {
		setError(error.message);
		setIsLoading(false);
	  }
	};
	getAllUser();
  }, [user_id, role_as]);

  const copyToClipboard = () => {
	const refLink = `https://dpworld.online/create-acount?p=${user_id}`;
	navigator.clipboard.writeText(refLink).then(() => {
	  setCopied(true);
	  setTimeout(() => setCopied(false), 2000);
	});
  };

  /* ── Role label ── */
  const roleLabel =
	user_role === 2 ? 'Admin' :
	user_role === 2.1 ? 'Affiliate Manager' :
	user_role === 4 ? 'Affiliate Agent' : '';

  /* ── Loading ── */
  if (!data) {
	return (
	  <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
		<div style={{ textAlign: 'center', color: '#BBBBBB' }}>
		  <i className="material-icons" style={{ fontSize: '36px', display: 'block', marginBottom: '8px' }}>hourglass_empty</i>
		  <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: '14px' }}>Loading dashboard…</span>
		</div>
	  </div>
	);
  }

  const bal = data[0] ? parseFloat(data[0].currency).toFixed(2) : '—';
  const commision_balance = data[0].deposit_percentage ? parseFloat(data[0].deposit_percentage).toFixed(2) : 0;
  const refLink = `https://dpworld.online/create-acount?p=${user_id}`;

  /* ── Stat cards by role ── */
  const renderStats = () => {
	if (user_role === 4) {
	  const dep = (agentDashboardData.total_user_deposit_balance || 0) + (agentDashboardData.total_user_deposit_rcvd || 0);
	  return (
		<div style={S.grid}>
		  <StatCard label="Current Balance" value={data[0]?.currency ?? '—'} unit="TK" iconClass="account_balance_wallet" iconBg="#EBF3FF" iconColor="#0066CC" />
		  <StatCard label="Monthly Deposit" value={dep} iconClass="arrow_downward" iconBg="#E8F5E9" iconColor="#2E7D32" linkTo="/daily-deposit-list-affiliate" />
		  <StatCard label="Monthly Withdraw" value={agentDashboardData.total_user_withdraw || 0} iconClass="arrow_upward" iconBg="#FFF3E0" iconColor="#E65100" linkTo="/daily-withdraw-list-affiliate" />
		  <StatCard label="Promotion Balance" value={agentDashboardData.total_Bonus || 0} iconClass="card_giftcard" iconBg="#FCE4EC" iconColor="#C2185B" />
		  <StatCard label="Total Users" value={agentDashboardData.total_agent_Downline_Count ?? '—'} iconClass="group" iconBg="#F3E5F5" iconColor="#7B1FA2" />
		  <StatCard label="Active Players" value={agentDashboardData.total_active_user ?? '—'} iconClass="sports_esports" iconBg="#E0F2F1" iconColor="#00796B" />
		</div>
	  );
	}
	if (user_role === 2.1) {
	  return (
		<div style={S.grid}>
		  <StatCard label="Current Balance" value={bal} unit="TK" iconClass="account_balance_wallet" iconBg="#EBF3FF" iconColor="#0066CC" />
		  <StatCard label="Withdraw Balance" value={commision_balance || 0} unit="TK" iconClass="percent" iconBg="#FCE4EC" iconColor="#C2185B" linkTo="affiliate-index" />
		   <StatCard label="Commission Balance" value={agentDashboardData.total_Downline_Balance *0.05 || 0} unit="TK" iconClass="percent" iconBg="#FCE4EC" iconColor="#C2185B" linkTo="affiliate-index" />
		  <StatCard label="Affiliate Commission" value={agentDashboardData.total_Downline_Balance || 0} unit="TK" iconClass="percent" iconBg="#FCE4EC" iconColor="#C2185B" linkTo="affiliate-index" />
		  <StatCard label="Affiliate Total Deposit" value={agentDashboardData.total_user_deposit_balance || 0} unit="TK" iconClass="arrow_downward" iconBg="#E8F5E9" iconColor="#2E7D32" />
		  <StatCard label="Affiliate Total Withdraw" value={agentDashboardData.total_user_withdraw || 0} unit="TK" iconClass="arrow_upward" iconBg="#FFF3E0" iconColor="#E65100" />
		</div>
	  );
	}
	if (user_role === 2) {
	  return (
		<div style={S.grid}>
		  <StatCard label="Current Balance" value={bal} unit="TK" iconClass="account_balance_wallet" iconBg="#EBF3FF" iconColor="#0066CC" />
		</div>
	  );
	}
	return null;
  };

  /* ── Action buttons by role ── */
  const renderActions = () => {
	if (user_role === 2) return (
	  <div style={S.bannerActions}>
		<Link to="/user-bal-tr" style={S.btn('#FFFFFF', '#0066CC')}>
		  <i className="material-icons" style={{ fontSize: '16px' }}>send</i> Banking
		</Link>
		<Link to="/subreseller-create" style={S.btn('#FFC107', '#111')}>
		  <i className="material-icons" style={{ fontSize: '16px' }}>add</i> Create Affiliate Manager
		</Link>
	  </div>
	);
	if (user_role === 2.1) return (
	  <div style={S.bannerActions}>
		<Link to="/affiliate-index" style={S.btn('rgba(255,255,255,0.15)', '#fff')}>
		  <i className="material-icons" style={{ fontSize: '16px' }}>person_add</i> Create Affiliate
		</Link>
		<Link to="/pending-balance-request" style={S.btn('rgba(255,255,255,0.15)', '#fff')}>
		  <i className="material-icons" style={{ fontSize: '16px' }}>arrow_downward</i> Deposit
		</Link>
		<Link to="/pending-withdraw-request" style={S.btn('rgba(255,255,255,0.15)', '#fff')}>
		  <i className="material-icons" style={{ fontSize: '16px' }}>arrow_upward</i> Withdraw
		</Link>
		{/* <Link to="/new-chats" style={S.btn('#E8380A', '#fff')}>
		  <i className="material-icons" style={{ fontSize: '16px' }}>chat</i> Chats
		</Link> */}
	  </div>
	);
	if (user_role === 4) return (
	  <div style={S.bannerActions}>
		<a href={refLink} style={S.btn('rgba(255,255,255,0.15)', '#fff')} target="_blank" rel="noopener noreferrer">
		  <i className="material-icons" style={{ fontSize: '16px' }}>person_add</i> Create User
		</a>
		<Link to="/balance-withdraw" style={S.btn('#E8380A', '#fff')}>
		  <i className="material-icons" style={{ fontSize: '16px' }}>payments</i> Withdraw
		</Link>
	  </div>
	);
	return null;
  };

  return (
	<>
	 	
	 <div className="content-body" style={{paddingTop:'0px'}}>
	  <div className="container-fluid">

	  <div style={S.page}>

		{/* ── Notice Scroll Bar ── */}
		<div style={{
		  background: '#0066CC',
		  borderRadius: '10px',
		  marginBottom: '14px',
		  padding: '0 16px',
		  height: '38px',
		  display: 'flex',
		  alignItems: 'center',
		  gap: '12px',
		  overflow: 'hidden',
		  boxShadow: '0 2px 8px rgba(0,102,204,0.2)',
		}}>
		  {/* Label pill */}
		  <span style={{
			background: '#E8380A',
			color: '#fff',
			fontSize: '10px',
			fontWeight: 800,
			letterSpacing: '0.08em',
			textTransform: 'uppercase',
			padding: '3px 9px',
			borderRadius: '20px',
			flexShrink: 0,
		  }}>LIVE</span>

		  {/* Scrolling text */}
		  <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
			<style>{`
			  @keyframes noticeScroll {
				0%   { transform: translateX(100%); }
				100% { transform: translateX(-100%); }
			  }
			  .notice-scroll-text {
				display: inline-block;
				white-space: nowrap;
				animation: noticeScroll 22s linear infinite;
				color: #fff;
				font-size: 13px;
				font-weight: 600;
				font-family: 'Outfit', sans-serif;
			  }
			  .notice-scroll-text:hover { animation-play-state: paused; }
			`}</style>
			<span className="notice-scroll-text">
			  🎉 Welcome to the {roleLabel} Panel &nbsp;&nbsp;•&nbsp;&nbsp;
			  Manage your downline agents and transactions here &nbsp;&nbsp;•&nbsp;&nbsp;
			  For support, use the New Chats feature &nbsp;&nbsp;•&nbsp;&nbsp;
			  Always verify transactions before approval &nbsp;&nbsp;&nbsp;&nbsp;
			</span>
		  </div>
		</div>

		{/* ── Welcome Banner ── */}
		<div style={S.banner}>
		  {/* decorative bubbles */}
		  <div style={{ ...S.bannerBubble, width: 180, height: 180, top: -60, right: 80 }} />
		  <div style={{ ...S.bannerBubble, width: 100, height: 100, bottom: -30, right: 20 }} />

		  <div style={S.bannerLeft}>
			<div style={S.bannerGreet}>Welcome back</div>
			<h2 style={S.bannerTitle}>
			  {roleLabel ? <><span style={S.bannerRole}>{roleLabel}</span> Panel</> : 'Dashboard'}
			</h2>
			<div style={S.bannerUID}>
			  <i className="material-icons" style={{ fontSize: '13px', verticalAlign: 'middle', marginRight: '4px' }}>badge</i>
			  {data[0]?.user_id}
			</div>
		  </div>

		  {renderActions()}
		</div>

		{/* ── Stat Cards ── */}
		{renderStats()}

		{/* ── Referral link (affiliate only) ── */}
		{user_role === 4 && (
		  <>
			<div style={S.sectionTitle}>Referral Link</div>
			<div style={S.refBox}>
			  <div style={S.refLabel}>Your referral link</div>
			  <div style={S.refRow}>
				<input
				  type="text"
				  readOnly
				  value={refLink}
				  style={S.refInput}
				/>
				<button
				  onClick={copyToClipboard}
				  style={{
					...S.btn(copied ? '#2E7D32' : '#0066CC'),
					flexShrink: 0,
				  }}
				>
				  <i className="material-icons" style={{ fontSize: '15px' }}>
					{copied ? 'check' : 'content_copy'}
				  </i>
				  {copied ? 'Copied!' : 'Copy'}
				</button>
			  </div>
			</div>
		  </>
		)}

		{/* ── Chat table ── */}
		{agentdata?.length > 0 && (
		  <>
			<div style={{ ...S.sectionTitle, marginTop: '22px' }}>Active Chats</div>
			<div style={S.tableWrap}>
			  <div style={S.tableHead}>
				<span>#</span>
				<span>User ID</span>
				<span>Connect</span>
			  </div>
			  {agentdata.map((item, index) => (
				<div style={S.tableRow} key={item.id}>
				  <span style={{ color: '#BBBBBB', fontWeight: 600 }}>{index + 1}</span>
				  <span style={{ fontWeight: 600, color: '#222' }}>{item.user_id}</span>
				  <Link to={`/chat-with/${item.user_id}`} style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
					<i className="material-icons" style={{ fontSize: '18px', color: '#0066CC' }}>chat_bubble</i>
					{item.unreadCount > 0 && (
					  <span style={S.badge('#FFECEC', '#E8380A')}>{item.unreadCount}</span>
					)}
				  </Link>
				</div>
			  ))}
			</div>
		  </>
		)}

	    </div>
	   </div>
	  </div>
	</>
  );
};

export default DashContent;
